import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./api";
import { useAuthStore } from "./auth";

/**
 * Teammate invitation + team-listing hooks.
 *
 *   useInviteTeammate()  — POST /tenants/me/invitations { email, role }
 *   useTeamMembers()     — GET  /tenants/me/team
 *
 * Used by the onboarding wizard's Invite step and Settings → Team.
 */

export type InviteRole = "ADMIN" | "MEMBER";

export function useInviteTeammate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; role: InviteRole }) =>
      unwrap(
        await api.POST("/api/v1/tenants/me/invitations", {
          body: { email: input.email, role: input.role },
        }),
      ),
    onSuccess: () => {
      // The new INVITED row should appear in both the pending-only
      // /invitations view and the full /team view.
      qc.invalidateQueries({ queryKey: ["tenant", "invitations"] });
      qc.invalidateQueries({ queryKey: ["tenant", "team"] });
    },
  });
}

export function useTeamMembers() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["tenant", "team"],
    enabled: !!accessToken,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/tenants/me/team", {})),
  });
}
